// Pure helpers for manual invoice entries: building the snapshot figures from
// form input and describing what changed when one is edited. No Firestore.
import { calculateExpectedReceipt, round } from "./whtCalculator";
import { LINE_CATEGORIES, makeLine } from "./lineCategories";

export const snapshotIdFor = (invoiceNumber) => encodeURIComponent(String(invoiceNumber || "").trim().toUpperCase());

export function buildManualFields(input) {
  const clientCode = String(input.clientCode || "").trim().toUpperCase();
  const invoiceNumber = String(input.invoiceNumber || "").trim();
  if (!clientCode) throw new Error("A client is required.");
  if (!invoiceNumber) throw new Error("An invoice number is required.");
  if (!input.invoiceDate) throw new Error("Enter the invoice date.");
  if (!input.accountingPeriod) throw new Error("Enter the accounting period (YYYY-MM).");
  if (!String(input.reason || "").trim()) throw new Error("Enter a reason this entry is manual — what actually happened differently from today's formulas.");
  const grossAmount = round(input.grossAmount);
  if (!(grossAmount > 0)) throw new Error("Gross amount must be greater than zero.");

  const serviceChargeSubtotal = round(input.serviceChargeSubtotal);
  const gstAmount = round(input.gstAmount);
  const whtAmount = round(input.whtAmount);
  const gstWithheldAmount = round(input.gstWithheldAmount);
  const recoveryAddBack = input.recoveryAddBack === "" || input.recoveryAddBack == null ? null : round(input.recoveryAddBack);
  const expectedReceipt = calculateExpectedReceipt({ grossAmount, whtAmount, gstWithheldAmount });

  return {
    sourceCollection: "manual_entry",
    sourceDocId: invoiceNumber,
    clientCode,
    invoiceNumber,
    monthKey: input.accountingPeriod,
    accountingPeriod: input.accountingPeriod,
    invoiceDate: input.invoiceDate,
    taxProfileId: null,
    taxProfileVersion: null,
    lines: [
      makeLine({ category: LINE_CATEGORIES.SERVICE_CHARGE, description: "Service charges (manual entry)", amount: serviceChargeSubtotal }),
      makeLine({ category: LINE_CATEGORIES.GST, description: "GST (manual entry)", amount: gstAmount })
    ],
    totals: { serviceChargeSubtotal, gstAmount, grossAmount, whtAmount, gstWithheldAmount, recoveryAddBack, expectedReceipt },
    whtModelUsed: input.whtModelUsed || null,
    recoveryVariance: null,
    recoveryVarianceOverridden: false,
    recoveryOverrideReason: null,
    recoveryOverrideBy: null,
    status: "APPROVED",
    enteredManually: true,
    manualEntryReason: String(input.reason).trim()
  };
}

// Turns a stored snapshot back into the form's fields, for editing.
export function formFromSnapshot(entry) {
  const totals = entry.totals || {};
  return {
    clientCode: entry.clientCode,
    invoiceNumber: entry.invoiceNumber,
    invoiceDate: entry.invoiceDate,
    accountingPeriod: entry.accountingPeriod,
    serviceChargeSubtotal: totals.serviceChargeSubtotal ?? "",
    gstAmount: totals.gstAmount ?? "",
    grossAmount: totals.grossAmount ?? "",
    whtAmount: totals.whtAmount ?? "",
    gstWithheldAmount: totals.gstWithheldAmount ?? "",
    recoveryAddBack: totals.recoveryAddBack ?? "",
    whtModelUsed: entry.whtModelUsed || "",
    reason: entry.manualEntryReason || ""
  };
}

const TRACKED = [
  ["Invoice number", (s) => s.invoiceNumber],
  ["Invoice date", (s) => s.invoiceDate],
  ["Accounting period", (s) => s.accountingPeriod],
  ["Service charge", (s) => s.totals?.serviceChargeSubtotal],
  ["GST", (s) => s.totals?.gstAmount],
  ["Gross amount", (s) => s.totals?.grossAmount],
  ["WHT", (s) => s.totals?.whtAmount],
  ["GST withheld", (s) => s.totals?.gstWithheldAmount],
  ["Expected receipt", (s) => s.totals?.expectedReceipt],
  ["Recovery add-back", (s) => s.totals?.recoveryAddBack ?? null],
  ["WHT model", (s) => s.whtModelUsed ?? null],
  ["Reason", (s) => s.manualEntryReason]
];

export function describeChanges(before, after) {
  return TRACKED
    .map(([field, pick]) => ({ field, from: pick(before) ?? null, to: pick(after) ?? null }))
    .filter((change) => String(change.from ?? "") !== String(change.to ?? ""));
}

const MONTH_NUMBERS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };

// Months named in free text, as YYYY-MM: "Jul-2026", "July 2026", "JUL-26" -> "2026-07".
// Used to warn when the text says one month but the P&L month is another.
export function descriptionMonths(text) {
  const found = new Set();
  const pattern = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*[\s\-/.,]*((?:20)?\d{2})\b/gi;
  let match = pattern.exec(String(text || ""));
  while (match) {
    const year = match[2].length === 2 ? 2000 + Number(match[2]) : Number(match[2]);
    found.add(`${year}-${String(MONTH_NUMBERS[match[1].toLowerCase()]).padStart(2, "0")}`);
    match = pattern.exec(String(text || ""));
  }
  return [...found];
}
